import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  CircularProgress,
  TableSortLabel,
  Chip,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

export type SortDirection = 'asc' | 'desc';

export interface Column<T> {
  id: keyof T | string;
  label: string;
  numeric?: boolean;
  sortable?: boolean;
  width?: string | number;
  render?: (value: any, row: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  // Pagination
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions?: number[];
  // Sorting
  sortBy?: keyof T | string;
  sortDirection?: SortDirection;
  onSort?: (column: keyof T | string) => void;
  // Row actions
  rowKey: keyof T;
  onRowClick?: (row: T) => void;
  // Empty state
  emptyMessage?: string;
  // Table size
  size?: 'small' | 'medium';
  // Sticky header
  stickyHeader?: boolean;
  // Max height
  maxHeight?: number | string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  loading = false,
  error = null,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  sortBy,
  sortDirection = 'asc',
  onSort,
  rowKey,
  onRowClick,
  emptyMessage = 'No data found',
  size = 'small',
  stickyHeader = true,
  maxHeight,
}: DataTableProps<T>) {
  const handleSort = (columnId: keyof T | string) => {
    if (onSort && columns.find(col => col.id === columnId)?.sortable !== false) {
      onSort(columnId);
    }
  };

  const renderCellContent = (column: Column<T>, row: T) => {
    const columnId = String(column.id);
    const value = columnId.includes('.') 
      ? columnId.split('.').reduce((obj, key) => obj?.[key], row as any)
      : row[column.id as keyof T];

    if (column.render) {
      return column.render(value, row);
    }

    // Default rendering for common types
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'boolean') {
      return (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          color={value ? 'success' : 'default'}
          size="small"
        />
      );
    }

    if (value instanceof Date) {
      return value.toLocaleString();
    }

    if (typeof value === 'number') {
      return column.numeric ? value.toLocaleString() : value;
    }

    return String(value);
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: maxHeight }}>
        <Table size={size} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id)}
                  align={column.align || (column.numeric ? 'right' : 'left')}
                  style={{ width: column.width, cursor: column.sortable !== false && onSort ? 'pointer' : 'default' }}
                >
                  {column.sortable !== false && onSort ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortDirection : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                      {sortBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={String(row[rowKey])}
                  hover
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {columns.map((column) => (
                    <TableCell 
                      key={String(column.id)} 
                      align={column.align || (column.numeric ? 'right' : 'left')}
                    >
                      {renderCellContent(column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </Paper>
  );
}
